import React, { useRef, useState } from 'react';
import { Spinner } from 'app/src/components/Spinner';
import { forTemplate } from 'app/common/lib/util/i18n';
import styles from './Upload.less';
import cx from 'classnames';
import {
  useSelectCoverQuery,
  useUploadCoverMutation,
} from '@trello/graphql/generated';
import { track } from '@trello/analytics';
import {
  Analytics,
  Action,
  Source,
  ActionSubject,
  ActionSubjectId,
} from '@trello/atlassian-analytics';

const format = forTemplate('card_cover_chooser');

interface UploadProps {
  cardId: string;
  onFileUploaded?: () => void;
  onFileSelected?: () => void;
  onUploadFailed?: () => void;
  hideHeading?: boolean;
  className?: string;
}

export const Upload: React.FunctionComponent<UploadProps> = ({
  cardId,
  onFileSelected,
  onFileUploaded,
  onUploadFailed,
  hideHeading,
  className,
}) => {
  const { data } = useSelectCoverQuery({
    variables: { cardId },
    fetchPolicy: 'cache-only',
  });

  const card = data?.card;
  const containers = {
    organization: {
      id: card?.board.idOrganization,
    },
    board: {
      id: card?.board.id,
    },
    card: {
      id: card?.id,
    },
  };

  const [uploadCover, { loading, error }] = useUploadCoverMutation({
    onError: onUploadFailed,
    onCompleted: () => {
      track('Card Cover Chooser', 'Set cover to an upload');

      Analytics.sendTrackEvent({
        action: Action.uploaded,
        actionSubject: ActionSubject.cover,
        source: Source.cardCoverInlineDialog,
        containers,
      });

      if (onFileUploaded) {
        onFileUploaded();
      }
    },
  });
  const [file, setFile] = useState<File | undefined>();
  const fileInput = useRef<HTMLInputElement>(null);

  if (loading) {
    return <Spinner centered />;
  }

  return (
    <div className={className}>
      {!hideHeading && <h4 className={styles.heading}>{format('upload')}</h4>}
      <input
        ref={fileInput}
        className={styles.fileInput}
        type="file"
        accept="image/*"
        onClick={() => {
          setFile(undefined);
        }}
        onChange={async (e) => {
          const input = e.target;
          const selectedFile = input.files && input.files[0];
          if (selectedFile) {
            setFile(selectedFile);

            if (onFileSelected) {
              onFileSelected();
            }

            uploadCover({
              variables: {
                cardId,
                file: selectedFile,
              },
            });
          }
        }}
      />

      <button
        className={cx('button', styles.upload)}
        onClick={() => {
          fileInput.current && fileInput.current.click();
          track('Card Cover Chooser', 'Clicked Upload');
          Analytics.sendClickedButtonEvent({
            buttonName: ActionSubjectId.cardCoverAttachmentUploadButton,
            source: Source.cardCoverInlineDialog,
            containers,
          });
        }}
      >
        {format('upload-a-cover-image')}
      </button>
      {error && file ? (
        <div className={styles.error}>
          {format('unable-to-use-file-as-card-cover', { fileName: file.name })}
        </div>
      ) : (
        <div className={styles.tip}>{format('tip-drag-an-image')}</div>
      )}
    </div>
  );
};
